import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  QrcodeOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  message,
  Popconfirm,
  QRCode,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api, { FRONTEND_URL } from '../config/api';
import { checkinAppUrl } from '../config/checkinAppUrl';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

interface CheckinAccessData {
  id: string;
  pin: string;
  name?: string;
  eventId: string;
  validFrom: string;
  validUntil: string;
  createdBy: string;
  createdByName?: string;
  usedCount: number;
  lastUsedAt?: string;
  isActive: boolean;
  createdAt: string;
}

interface CheckinAccessProps {
  eventId: string;
  eventTitle: string;
  /**
   * Az esemény nyelve (`Event.language`), hogy a kiírt ajtó-link a megfelelő
   * nyelven nyíljon. Hiányában a link a magyar alakot kapja — pontosan azt,
   * amit a belépőképernyő paraméter nélkül is ad.
   *
   * **Szándékosan KÖTELEZŐ, `undefined`-ot megengedve.** Opcionális propként a
   * hívó oldalról nyom nélkül eltűnhetne, és minden holland ajtó-link némán
   * elveszítené a `?lang=nl`-t — a panelen továbbra is egy hibátlan alakú cím
   * állna. Így viszont a typecheck kéri számon a hívón, hogy döntsön róla.
   */
  eventLanguage: string | undefined;
}

const CheckinAccess: React.FC<CheckinAccessProps> = ({ eventId, eventTitle, eventLanguage }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [accesses, setAccesses] = useState<CheckinAccessData[]>([]);
  const [statistics, setStatistics] = useState<any>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [selectedAccess, setSelectedAccess] = useState<CheckinAccessData | null>(null);
  const [editingAccess, setEditingAccess] = useState<CheckinAccessData | null>(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    fetchAccesses();
    fetchStatistics();
  }, [eventId]);

  const fetchAccesses = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/checkin-access/event/${eventId}`);
      setAccesses(response.data);
    } catch (error) {
      message.error(t('checkinAccess.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get(`/checkin-access/statistics/${eventId}`);
      setStatistics(response.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  const handleCreateAccess = async (values: any) => {
    try {
      const data: any = {
        eventId,
        name: values.name,
      };

      if (values.validRange && values.validRange.length === 2) {
        data.validFrom = values.validRange[0].toISOString();
        data.validUntil = values.validRange[1].toISOString();
      }

      const response = await api.post('/checkin-access', data);
      message.success(t('checkinAccess.createSuccess'));
      setIsModalVisible(false);
      form.resetFields();
      fetchAccesses();

      // Show the PIN in a modal
      Modal.success({
        title: t('checkinAccess.createdTitle'),
        content: (
          <div style={{ textAlign: 'center' }}>
            <Title level={2} style={{ marginTop: 20 }}>
              {t('checkinAccess.pinValue', { pin: response.data.pin })}
            </Title>
            <Text type="secondary">{values.name || t('checkinAccess.defaultName')}</Text>
            <div style={{ marginTop: 20 }}>
              <Button
                icon={<CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(response.data.pin);
                  message.success(t('checkinAccess.pinCopied'));
                }}
              >
                {t('checkinAccess.copyPin')}
              </Button>
            </div>
          </div>
        ),
        width: 400,
      });
    } catch (error) {
      message.error(t('checkinAccess.createFailed'));
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await api.delete(`/checkin-access/${id}`);
      message.success(t('checkinAccess.deactivateSuccess'));
      fetchAccesses();
    } catch (error) {
      message.error(t('checkinAccess.deactivateFailed'));
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await api.patch(`/checkin-access/${id}`, { isActive: true });
      message.success(t('checkinAccess.activateSuccess'));
      fetchAccesses();
    } catch (error) {
      message.error(t('checkinAccess.activateFailed'));
    }
  };

  const handleEdit = (access: CheckinAccessData) => {
    setEditingAccess(access);
    editForm.setFieldsValue({
      name: access.name,
      validUntil: dayjs(access.validUntil),
    });
    setIsEditModalVisible(true);
  };

  const handleUpdateAccess = async (values: any) => {
    if (!editingAccess) return;

    try {
      const data: any = {
        name: values.name,
      };

      if (values.validUntil) {
        data.validUntil = values.validUntil.toISOString();
      }

      await api.patch(`/checkin-access/${editingAccess.id}`, data);
      message.success(t('checkinAccess.updateSuccess'));
      setIsEditModalVisible(false);
      editForm.resetFields();
      setEditingAccess(null);
      fetchAccesses();
    } catch (error) {
      message.error(t('checkinAccess.updateFailed'));
    }
  };

  const showPinModal = (access: CheckinAccessData) => {
    Modal.info({
      title: t('checkinAccess.pinTitle'),
      content: (
        <div style={{ textAlign: 'center' }}>
          <Title level={2} style={{ marginTop: 20 }}>
            {t('checkinAccess.pinValue', { pin: access.pin })}
          </Title>
          <Text type="secondary">{access.name || t('checkinAccess.defaultName')}</Text>
          <div style={{ marginTop: 20 }}>
            <QRCode value={`CHECKIN:${access.pin}`} size={200} />
          </div>
          <div style={{ marginTop: 20 }}>
            <Space>
              <Button
                icon={<CopyOutlined />}
                onClick={() => {
                  navigator.clipboard.writeText(access.pin);
                  message.success(t('checkinAccess.pinCopied'));
                }}
              >
                {t('checkinAccess.copyPin')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={() => {
                  // Download QR code
                  const canvas = document.querySelector('.ant-qrcode canvas') as HTMLCanvasElement;
                  if (canvas) {
                    const url = canvas.toDataURL('image/png');
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `checkin-${access.pin}.png`;
                    a.click();
                  }
                }}
              >
                {t('checkinAccess.downloadQr')}
              </Button>
            </Space>
          </div>
          <div style={{ marginTop: 20, textAlign: 'left' }}>
            <Text strong>{t('checkinAccess.validFrom')}</Text>{' '}
            {dayjs(access.validFrom).format('YYYY-MM-DD HH:mm')}
            <br />
            <Text strong>{t('checkinAccess.validUntil')}</Text>{' '}
            {dayjs(access.validUntil).format('YYYY-MM-DD HH:mm')}
            <br />
            <Text strong>{t('checkinAccess.usedCount')}</Text> {access.usedCount}
            {access.lastUsedAt && (
              <>
                <br />
                <Text strong>{t('checkinAccess.lastUsed')}</Text>{' '}
                {dayjs(access.lastUsedAt).format('YYYY-MM-DD HH:mm')}
              </>
            )}
          </div>
        </div>
      ),
      width: 500,
    });
  };

  const columns = [
    {
      title: t('checkinAccess.columnName'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => name || <Text type="secondary">{t('checkinAccess.unnamed')}</Text>,
    },
    {
      title: t('checkinAccess.columnPin'),
      dataIndex: 'pin',
      key: 'pin',
      render: (pin: string, record: CheckinAccessData) => (
        <Button type="link" icon={<QrcodeOutlined />} onClick={() => showPinModal(record)}>
          {t('checkinAccess.viewPin')}
        </Button>
      ),
    },
    {
      title: t('checkinAccess.columnValidity'),
      key: 'validity',
      render: (record: CheckinAccessData) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>
            {t('checkinAccess.fromShort', {
              date: dayjs(record.validFrom).format('YYYY-MM-DD HH:mm'),
            })}
          </Text>
          <Text style={{ fontSize: 12 }}>
            {t('checkinAccess.untilShort', {
              date: dayjs(record.validUntil).format('YYYY-MM-DD HH:mm'),
            })}
          </Text>
        </Space>
      ),
    },
    {
      title: t('checkinAccess.columnStatus'),
      key: 'status',
      render: (record: CheckinAccessData) => {
        const now = dayjs();
        const validFrom = dayjs(record.validFrom);
        const validUntil = dayjs(record.validUntil);

        if (!record.isActive) {
          return <Tag color="default">{t('checkinAccess.statusInactive')}</Tag>;
        } else if (now.isBefore(validFrom)) {
          return <Tag color="orange">{t('checkinAccess.statusNotYetValid')}</Tag>;
        } else if (now.isAfter(validUntil)) {
          return <Tag color="red">{t('checkinAccess.statusExpired')}</Tag>;
        } else {
          return <Tag color="green">{t('checkinAccess.statusActive')}</Tag>;
        }
      },
    },
    {
      title: t('checkinAccess.columnUsage'),
      key: 'usage',
      render: (record: CheckinAccessData) => (
        <Space>
          <Text>{t('checkinAccess.scans', { count: record.usedCount })}</Text>
          {record.lastUsedAt && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('checkinAccess.lastShort', {
                date: dayjs(record.lastUsedAt).format('MM-DD HH:mm'),
              })}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: t('checkinAccess.columnCreatedBy'),
      dataIndex: 'createdByName',
      key: 'createdByName',
      render: (name: string) => name || t('checkinAccess.unknownCreator'),
    },
    {
      title: t('checkinAccess.columnActions'),
      key: 'actions',
      render: (record: CheckinAccessData) => (
        <Space>
          <Button size="small" onClick={() => showPinModal(record)} icon={<QrcodeOutlined />}>
            {t('checkinAccess.view')}
          </Button>
          <Button size="small" onClick={() => handleEdit(record)} icon={<EditOutlined />}>
            {t('checkinAccess.edit')}
          </Button>
          {record.isActive ? (
            <Popconfirm
              title={t('checkinAccess.confirmDeactivate')}
              onConfirm={() => handleDeactivate(record.id)}
            >
              <Button size="small" danger icon={<CloseCircleOutlined />}>
                {t('checkinAccess.deactivate')}
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title={t('checkinAccess.confirmActivate')}
              onConfirm={() => handleActivate(record.id)}
            >
              <Button size="small" type="primary" icon={<CheckCircleOutlined />}>
                {t('checkinAccess.activate')}
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('checkinAccess.statTotalTickets')}
              value={statistics?.totalTickets || 0}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('checkinAccess.statCheckedIn')}
              value={statistics?.checkedIn || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('checkinAccess.statRemaining')}
              value={(statistics?.totalTickets || 0) - (statistics?.checkedIn || 0)}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('checkinAccess.statActiveAccessPoints')}
              value={accesses.filter((a) => a.isActive).length}
              prefix={<QrcodeOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Check-in App Link */}
      <Alert
        message={t('checkinAccess.appAlertTitle')}
        description={
          <Space direction="vertical">
            <Text>{t('checkinAccess.appAlertBody')}</Text>
            <Text strong copyable>
              {checkinAppUrl(FRONTEND_URL, eventLanguage)}
            </Text>
          </Space>
        }
        type="info"
        showIcon
        icon={<QrcodeOutlined />}
        style={{ marginBottom: 24 }}
      />

      {/* Table Header */}
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
          {t('checkinAccess.generatePin')}
        </Button>
      </div>

      {/* Accesses Table */}
      <Table
        columns={columns}
        dataSource={accesses}
        rowKey="id"
        loading={loading}
        pagination={{ defaultPageSize: 10 }}
      />

      {/* Create Access Modal */}
      <Modal
        title={t('checkinAccess.createModalTitle')}
        visible={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateAccess}>
          <Form.Item
            name="name"
            label={t('checkinAccess.nameLabelOptional')}
            extra={t('checkinAccess.nameHint')}
          >
            <Input placeholder={t('checkinAccess.namePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="validRange"
            label={t('checkinAccess.validRangeLabel')}
            extra={t('checkinAccess.validRangeHint')}
          >
            <RangePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
          </Form.Item>
        </Form>

        <Alert
          message={t('checkinAccess.howItWorks')}
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>{t('checkinAccess.howStep1')}</li>
              <li>{t('checkinAccess.howStep2')}</li>
              <li>{t('checkinAccess.howStep3')}</li>
              <li>{t('checkinAccess.howStep4')}</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Modal>

      {/* Edit Access Modal */}
      <Modal
        title={t('checkinAccess.editModalTitle')}
        visible={isEditModalVisible}
        onOk={() => editForm.submit()}
        onCancel={() => {
          setIsEditModalVisible(false);
          editForm.resetFields();
          setEditingAccess(null);
        }}
        width={600}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateAccess}>
          <Form.Item
            name="name"
            label={t('checkinAccess.nameLabel')}
            extra={t('checkinAccess.nameHint')}
          >
            <Input placeholder={t('checkinAccess.namePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="validUntil"
            label={t('checkinAccess.validUntilLabel')}
            rules={[{ required: true, message: t('checkinAccess.validUntilRequired') }]}
            extra={
              editingAccess
                ? t('checkinAccess.currentlyValidFrom', {
                    date: dayjs(editingAccess.validFrom).format('YYYY-MM-DD HH:mm'),
                  })
                : ''
            }
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm"
              placeholder={t('checkinAccess.validUntilPlaceholder')}
            />
          </Form.Item>
        </Form>

        <Alert
          message={t('checkinAccess.updateInfo')}
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>{t('checkinAccess.updateStep1')}</li>
              <li>{t('checkinAccess.updateStep2')}</li>
              <li>{t('checkinAccess.updateStep3')}</li>
              <li>{t('checkinAccess.updateStep4')}</li>
            </ul>
          }
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Modal>
    </div>
  );
};

export default CheckinAccess;
